import type { WebhookEvent } from '@cloudFunctions/functions/payments/types';
import type { WhereFilterOp } from 'firebase/firestore';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { useQuery } from '@tanstack/react-query';
import { firebase } from '@/lib/firebaseConfig';
import { finixKeys } from './queryKeys';

export type FinixWebhookEventWhereClause = [
  fieldPath: string,
  op: WhereFilterOp,
  value: unknown,
];

export interface UseFinixWebhookEventsOptions {
  whereClauses?: FinixWebhookEventWhereClause[];
  orderByField?: 'occurred_at';
  orderDirection?: 'asc' | 'desc';
}

export const useFinixWebhookEvents = (
  options: UseFinixWebhookEventsOptions | FinixWebhookEventWhereClause[] = {},
) => {
  const {
    whereClauses = [],
    orderByField,
    orderDirection = 'desc',
  } = Array.isArray(options) ? { whereClauses: options } : options;

  const queryResult = useQuery({
    queryKey: finixKeys.webhookEvents(
      whereClauses,
      orderByField,
      orderDirection,
    ),
    queryFn: async () => {
      const collectionRef = collection(
        firebase.firestore,
        'finixWebhookEvents',
      );
      let q = query(collectionRef);

      for (const [fieldPath, op, value] of whereClauses) {
        q = query(q, where(fieldPath, op, value));
      }

      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as WebhookEvent[];
    },
  });

  return {
    webhookEvents: queryResult.data ?? [],
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};
